import { memo, useCallback, useState, useEffect } from 'react';
import { Box, Paper, Typography, Button, TextField, Chip, ToggleButtonGroup, ToggleButton, Tooltip } from '@mui/material';
import { Add as AddIcon, Refresh as RefreshIcon, PlayArrow as PlayArrowIcon, Save as SaveIcon, FilterAlt as FilterAltIcon, Tune as TuneIcon } from '@mui/icons-material';
import { QueryGroup, QueryField, QueryRule, QueryExecutionResultWithConditions } from '../../../../types/QueryBuilder';
import { useLanguage } from '../../../../context/LanguageContext';
import QueryRuleGroup from './QueryRuleGroup';
import SimpleQueryGroup from './SimpleQueryGroup';
import GroupSelector from './GroupSelector';
import ConfirmDialog from '../../ListImport/components/ConfirmDialog';

export type QueryBuilderMode = 'simple' | 'advanced';

interface CreateQueryTabProps {
    mode: QueryBuilderMode;
    onModeChange: (mode: QueryBuilderMode) => void;
    groups: QueryGroup[];
    categories: string[];
    fields: QueryField[];
    simpleFields: QueryField[];
    simpleRules: QueryRule[];
    onUpdateSimpleField: (fieldName: string, data: Partial<QueryRule>) => void;
    queryJSON: QueryExecutionResultWithConditions | null;
    isValid: boolean;
    executingQuery: boolean;
    saving: boolean;
    selectedGroupId: number | null;
    newGroupName?: string;
    availableGroups: Array<{ id: number; description: string }>;
    loadingGroups: boolean;
    descResetKey?: number;
    onRefetchGroups: () => void | Promise<void>;
    onQueryDescriptionChange: (description: string) => void;
    onGroupIdChange: (groupId: number | null, groupName?: string) => void;
    onCreateGroup: (groupName: string) => void | Promise<void>;
    onAddRule: (groupId: string) => void;
    onUpdateRule: (groupId: string, ruleId: string, data: Partial<QueryRule>) => void;
    onRemoveRule: (groupId: string, ruleId: string) => void;
    onAddGroup: () => void;
    onRemoveGroup: (groupId: string) => void;
    onReset: () => void;
    onExecute: () => void;
    onSave: () => void;
}

const DescriptionField = memo(({
    value,
    onChange,
}: {
    value: string;
    onChange: (value: string) => void;
}) => {
    const { t } = useLanguage();
    return (
        <TextField
            size="small"
            label={t('pages.clients.queryBuilder.queryNameRequired')}
            name="queryDescription"
            value={value}
            onChange={(event) => onChange(event.target.value)}
            placeholder={t('pages.clients.queryBuilder.queryNamePlaceholder')}
            sx={{ flexGrow: 1, flexBasis: '220px', minWidth: 0 }}
        />
    );
});

interface QueryToolbarProps {
    queryDescription: string;
    onDescriptionChange: (value: string) => void;
    selectedGroupId: number | null;
    newGroupName?: string;
    availableGroups: Array<{ id: number; description: string }>;
    loadingGroups: boolean;
    onGroupIdChange: (groupId: number | null, groupName?: string) => void;
    onCreateGroup: (groupName: string) => void | Promise<void>;
    onRefetchGroups: () => void | Promise<void>;
    onReset: () => void;
    onExecute: () => void;
    onSave: () => void;
    executingQuery: boolean;
    saving: boolean;
    isValid: boolean;
}

// Rendered both above and below the rule builder so the user never has to scroll back up to
// name/save/execute a long query.
function QueryToolbar({
    queryDescription,
    onDescriptionChange,
    selectedGroupId,
    newGroupName,
    availableGroups,
    loadingGroups,
    onGroupIdChange,
    onCreateGroup,
    onRefetchGroups,
    onReset,
    onExecute,
    onSave,
    executingQuery,
    saving,
    isValid,
}: QueryToolbarProps) {
    const { t } = useLanguage();
    const hasDescription = queryDescription.trim().length > 0;

    const handleExecute = () => {
        if (hasDescription) onExecute();
    };

    const handleSave = () => {
        if (hasDescription) onSave();
    };

    return (
        <Paper elevation={0} sx={{ p: 2, mb: 2, border: '1px solid #e0e0e0' }}>
            <form>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center', mb: 2 }}>
                    <DescriptionField value={queryDescription} onChange={onDescriptionChange} />

                    <Box sx={{ flexGrow: 1, flexBasis: '180px', minWidth: 0 }}>
                        <GroupSelector
                            selectedGroupId={selectedGroupId}
                            newGroupName={newGroupName}
                            availableGroups={availableGroups}
                            loading={loadingGroups}
                            fullWidth
                            onGroupIdChange={onGroupIdChange}
                            onCreateGroup={onCreateGroup}
                            onRefetchGroups={onRefetchGroups}
                        />
                    </Box>

                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, ml: { xs: 0, lg: 'auto' } }}>
                        <Button
                            variant="outlined"
                            startIcon={<RefreshIcon />}
                            onClick={onReset}
                            sx={{ textTransform: 'none', borderColor: '#6c757d', color: '#6c757d' }}
                        >
                            {t('pages.clients.queryBuilder.reset')}
                        </Button>
                        <Tooltip title={!hasDescription ? t('pages.clients.queryBuilder.provideQueryNameWarning') : ''}>
                            <span>
                                <Button
                                    variant="contained"
                                    startIcon={<PlayArrowIcon />}
                                    onClick={handleExecute}
                                    disabled={executingQuery || !isValid || !hasDescription}
                                    sx={{ textTransform: 'none', bgcolor: '#556ee6' }}
                                >
                                    {executingQuery ? t('pages.clients.queryBuilder.executing') : t('pages.clients.queryBuilder.execute')}
                                </Button>
                            </span>
                        </Tooltip>
                        <Button
                            variant="outlined"
                            startIcon={<SaveIcon />}
                            onClick={handleSave}
                            disabled={saving || !isValid || !hasDescription}
                            sx={{ textTransform: 'none', color: '#28a745', borderColor: '#28a745' }}
                        >
                            {saving ? t('pages.clients.queryBuilder.saving') : t('pages.clients.queryBuilder.save')}
                        </Button>
                    </Box>
                </Box>
            </form>
        </Paper>
    );
}

export function CreateQueryTab({
    mode,
    onModeChange,
    groups,
    categories,
    fields,
    simpleFields,
    simpleRules,
    onUpdateSimpleField,
    queryJSON,
    isValid,
    executingQuery,
    saving,
    selectedGroupId,
    newGroupName,
    availableGroups,
    loadingGroups,
    descResetKey,
    onRefetchGroups,
    onQueryDescriptionChange,
    onGroupIdChange,
    onCreateGroup,
    onAddRule,
    onUpdateRule,
    onRemoveRule,
    onAddGroup,
    onRemoveGroup,
    onReset,
    onExecute,
    onSave,
}: CreateQueryTabProps) {
    const { t } = useLanguage();
    const [queryDescription, setQueryDescription] = useState('');
    const [resetModalOpen, setResetModalOpen] = useState(false);

    useEffect(() => {
        setQueryDescription('');
    }, [descResetKey]);

    const handleDescriptionChange = useCallback((v: string) => {
        setQueryDescription(v);
        onQueryDescriptionChange(v);
    }, [onQueryDescriptionChange]);

    const handleResetClick = useCallback(() => {
        setResetModalOpen(true);
    }, []);

    const handleResetConfirm = useCallback(() => {
        setResetModalOpen(false);
        onReset();
    }, [onReset]);

    const toolbar = (
        <QueryToolbar
            queryDescription={queryDescription}
            onDescriptionChange={handleDescriptionChange}
            selectedGroupId={selectedGroupId}
            newGroupName={newGroupName}
            availableGroups={availableGroups}
            loadingGroups={loadingGroups}
            onGroupIdChange={onGroupIdChange}
            onCreateGroup={onCreateGroup}
            onRefetchGroups={onRefetchGroups}
            onReset={handleResetClick}
            onExecute={onExecute}
            onSave={onSave}
            executingQuery={executingQuery}
            saving={saving}
            isValid={isValid}
        />
    );

    return (
        <Box>
            <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
                <ToggleButtonGroup
                    value={mode}
                    exclusive
                    onChange={(_, newMode: QueryBuilderMode | null) => newMode && onModeChange(newMode)}
                    sx={{
                        '& .MuiToggleButton-root': { textTransform: 'none', px: 3, fontWeight: 500 },
                        '& .MuiToggleButton-root.Mui-selected': { bgcolor: '#556ee6', color: 'white', '&:hover': { bgcolor: '#4457c9' } }
                    }}
                >
                    <ToggleButton value="simple">
                        <FilterAltIcon sx={{ mr: 1 }} fontSize="small" />
                        {t('pages.clients.queryBuilder.createQueryTab.simpleBuilder')}
                    </ToggleButton>
                    <ToggleButton value="advanced">
                        <TuneIcon sx={{ mr: 1 }} fontSize="small" />
                        {t('pages.clients.queryBuilder.createQueryTab.advancedBuilder')}
                    </ToggleButton>
                </ToggleButtonGroup>
            </Box>

            {toolbar}

            {mode === 'simple' ? (
                <SimpleQueryGroup
                    fields={simpleFields}
                    rules={simpleRules}
                    onUpdateField={onUpdateSimpleField}
                />
            ) : (
                <>
                    {groups.map((group, index) => (
                        <Box key={group.id}>
                            <QueryRuleGroup
                                group={group}
                                categories={categories}
                                fields={fields}
                                onAddRule={onAddRule}
                                onUpdateRule={onUpdateRule}
                                onRemoveRule={onRemoveRule}
                                onRemoveGroup={onRemoveGroup}
                                showCombinator={index > 0}
                                canRemoveGroup={groups.length > 1}
                            />
                            {index < groups.length - 1 && (
                                <Box sx={{ textAlign: 'center', my: 2 }}>
                                    <Chip label={t('pages.clients.queryBuilder.or')} sx={{ bgcolor: '#ffc107', color: 'white', fontWeight: 600 }} />
                                </Box>
                            )}
                        </Box>
                    ))}

                    {groups.length < 2 && (
                        <Button
                            fullWidth
                            variant="outlined"
                            startIcon={<AddIcon />}
                            onClick={onAddGroup}
                            sx={{ mt: 2, textTransform: 'none', borderColor: '#556ee6', color: '#556ee6', borderStyle: 'dashed' }}
                        >
                            {t('pages.clients.queryBuilder.addGroup')}
                        </Button>
                    )}
                </>
            )}

            {toolbar}

            {queryJSON && (
                <Paper elevation={0} sx={{ p: 2, mt: 3, border: '1px solid #e0e0e0' }}>
                    {queryJSON.count !== undefined && (
                        <Box sx={{ mb: 2, p: 2, bgcolor: '#e3f2fd', borderRadius: 1, border: '1px solid #2196f3' }}>
                            <Typography variant="h6" sx={{ color: '#1976d2', fontWeight: 600 }}>
                                🎯 {t('pages.clients.queryBuilder.resultsFound', { count: queryJSON.count })}
                            </Typography>
                        </Box>
                    )}
                    <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                        {t('pages.clients.queryBuilder.createQueryTab.jsonLabel')}
                    </Typography>
                    <Box
                        component="pre"
                        sx={{ bgcolor: '#f5f5f5', p: 2, borderRadius: 1, overflow: 'auto', fontSize: '0.875rem', maxHeight: '400px' }}
                    >
                        {JSON.stringify(queryJSON, null, 2)}
                    </Box>
                </Paper>
            )}

            <ConfirmDialog
                open={resetModalOpen}
                title={t('pages.clients.queryBuilder.createQueryTab.resetTitle')}
                message={t('pages.clients.queryBuilder.createQueryTab.resetMessage')}
                confirmText={t('pages.clients.queryBuilder.reset')}
                confirmColor="error"
                onConfirm={handleResetConfirm}
                onCancel={() => setResetModalOpen(false)}
            />
        </Box>
    );
}

export default CreateQueryTab;
